import { useFormikContext } from 'formik'
import React, { ReactElement, useEffect, useState } from 'react'

import amplitude from '@lib/analytics/amplitude'
import { ViewCheckoutPageData } from '@lib/analytics/amplitude/checkout'
import emarsys from '@lib/analytics/emarsys'
import GTMDataLayer from '@lib/analytics/gtm/GTMdataLayer'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useFinalPrice } from '@pages/Checkout/hooks/usePrice'
import { PriceHintsCheckoutData } from '@pages/Checkout/hooks/usePriceHints'
import { useSettings } from '@queries/settings'
import { CheckoutStore, useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'

interface Props {
  priceHintsData: PriceHintsCheckoutData
}

const getPriceHintsValue = ({
  loader,
  isVisible,
  lowerThanUsual,
}: PriceHintsCheckoutData): ViewCheckoutPageData['priceHint'] => {
  if (!loader.data) return undefined

  if (!lowerThanUsual) return 'AboveUsual'
  if (isVisible) return 'Visible'
  return 'Hidden'
}

type CombinedForQuantity = Pick<CheckoutFormData, 'passengers'> & Pick<CheckoutStore, 'outbound' | 'inbound'>

const getQuantity = ({ passengers, outbound, inbound }: CombinedForQuantity) => {
  return [outbound, inbound].filter(Boolean).length * passengers.length
}

const CheckoutAnalytics = ({ priceHintsData }: Props): ReactElement => {
  const [{ price: initialPrice, currency, retailerPartnerNumber }] = useParams()
  const [{ outbound, inbound }] = useCheckout()
  const [{ reservation }] = useSettings()
  const { values } = useFormikContext<CheckoutFormData>()
  const { isReservationLoading, isVacancyLoading, priceError, reservationData, vacancy } = values
  const [priceLoaded, setPriceLoaded] = useState<boolean>(false)

  const price = values.price ?? reservationData?.price

  const finalPrice = useFinalPrice()

  useEffect(() => {
    if (!isReservationLoading && !isVacancyLoading) {
      setPriceLoaded(true)
    }
  }, [isReservationLoading, isVacancyLoading])

  useEffect(() => {
    const isVacant = reservation.enabled ? reservationData?.price?.fractional != null : vacancy?.vacant

    if (outbound && priceLoaded && !priceHintsData.loader.isLoading) {
      amplitude.checkout.viewPage({
        priceHint: getPriceHintsValue(priceHintsData),
        outbound,
        inbound,
        fareClass: values.fareClass,
        passengers: values.passengers,
        initialPrice,
        vacant: priceError == null ? !!isVacant : false,
        price,
        reason: priceError == null ? values.vacancy?.reason?.code : 'API Error',
        errorData: priceError,
        ancillaries: values.vacancy?.ancillaries.map(({ code, price, name }) => ({ code, price, name })),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outbound, priceLoaded, priceHintsData.loader.isLoading])

  useEffect(() => {
    if (!outbound) return

    GTMDataLayer.pushCheckoutEvent({
      outbound,
      inbound,
      step: 1,
      currency,
      retailerPartnerNumber,
      fareClass: values.fareClass,
      pax: values.passengers.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outbound])

  useEffect(() => {
    if (!price || !outbound) return

    GTMDataLayer.pushCheckoutEvent({
      outbound,
      inbound,
      step: 2,
      currency,
      retailerPartnerNumber,
      fareClass: values.fareClass,
      pax: values.passengers.length,
      price: price,
      fees: values.fees,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, outbound])

  useEffect(() => {
    if (!values?.contact?.email) return

    emarsys.checkout.setEmail(values?.contact?.email)
  }, [values.contact.email])

  useEffect(() => {
    if (!values?.seats) return

    emarsys.checkout.sendSeats(values.seats)
  }, [values.seats])

  useEffect(() => {
    /* istanbul ignore if */
    if (!values?.ancillaries) return

    emarsys.checkout.sendAncillaries(values.ancillaries)
  }, [values.ancillaries])

  useEffect(() => {
    if (!outbound?.id || !finalPrice?.fractional) return

    const quantity = getQuantity({ passengers: values.passengers, inbound, outbound })

    /* istanbul ignore if */
    if (quantity === 0) return

    const cartItem = {
      item: outbound?.id,
      price: finalPrice?.fractional / 100,
      quantity: quantity,
      currency,
    }

    emarsys.checkout.sendCart([cartItem])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPrice?.fractional, currency])

  return <></>
}

export default CheckoutAnalytics
